import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import VerticalNavigation from "../../components/verticalNavigation/verticalNavigation.tsx";
import Dropdown from "../../components/dropdown/dropdown.tsx";
import StyleButton from "../../components/styleButton/styleButton.tsx";
import { Fuel, Leaf, Droplets, Bug } from "lucide-react";
import { useAppContext } from "../../data/context.tsx";
import type { TagState } from "../../data/types";
import { getWasteCategoriesWithTypesAndModels, getAllValueAddedProducts } from "../../services/analysisService.js";
import "./businessModels.css";

const BusinessModels: React.FC = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const {
    state,
    setWasteCategory,
    setWasteType,
    setBusinessModel,
    toggleBusinessModel,
  } = useAppContext();

  // State for backend data
  const [wasteCategories, setWasteCategories] = useState<string[]>([]);
  const [wasteTypes, setWasteTypes] = useState<{ [key: string]: string[] }>({});
  const [businessModels, setBusinessModels] = useState<{ [key: string]: string[] }>({});
  const [valueAddedProducts, setValueAddedProducts] = useState<any[]>([]);
  const [fullWasteData, setFullWasteData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Load data from backend
  useEffect(() => {
    const loadData = async () => {
      if (!state.selectedCountry) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Load waste categories with types and business models
        const wasteResponse = await getWasteCategoriesWithTypesAndModels(state.selectedCountry);
        const wasteData = wasteResponse.data;
        
        // Extract categories
        const categories = wasteData.map((category: any) => category.name);
        setWasteCategories(categories);
        
        // Extract waste types 
        const typesMap: { [key: string]: string[] } = {};
        
        wasteData.forEach((category: any) => {
          typesMap[category.name] = category.wasteTypes.map((type: any) => type.name);
        });
        
        setWasteTypes(typesMap);
        
        // Store the full waste data for business model mapping
        setFullWasteData(wasteData);
        
        // Load value added products
        const productsResponse = await getAllValueAddedProducts();
        setValueAddedProducts(productsResponse.data);
        
      } catch (err: any) {
        console.error('Error loading data:', err);
        setError(err.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [state.selectedCountry]);

  const getBusinessModelsForWasteType = (wasteType: string): string[] => {
    if (
      !state.selectedWasteCategory ||
      !wasteType ||
      !fullWasteData.length ||
      !state.selectedBusinessModel
    ) {
      return [];
    }
    const category = fullWasteData.find(
      (cat) => cat.name === state.selectedWasteCategory
    );
    if (!category) return [];
    const type = category.wasteTypes.find((t: any) => t.name === wasteType);
    if (!type || !Array.isArray(type.valueAddedProducts)) return [];
    const vap = type.valueAddedProducts.find(
      (p: any) => p.name === state.selectedBusinessModel
    );
    if (!vap || !Array.isArray(vap.businessModels)) return [];
    return vap.businessModels.map((model: any) => model);
  };

  const getCurrentBusinessModels = () => {
    if (!state.selectedWasteType || !state.selectedBusinessModel) {
      return [];
    }
    return getBusinessModelsForWasteType(state.selectedWasteType);
  };

  const isShowingDefault = !state.selectedBusinessModel;

  const tags: TagState[] = [
    {
      label: "Country",
      checked: !!state.selectedCountry,
      value: state.selectedCountry || undefined,
    },
    {
      label: "Region",
      checked: !!state.selectedRegion,
      value: state.selectedRegion || undefined,
    },
    {
      label: "District",
      checked: !!state.selectedDistrict,
      value: state.selectedDistrict || undefined,
    },
    {
      label: "Goal",
      checked: !!state.selectedObjective,
      value: state.selectedObjective || undefined,
    },
    {
      label: "Category",
      checked: !!state.selectedWasteCategory,
      value: state.selectedWasteCategory || undefined,
    },
    {
      label: "Waste Type",
      checked: !!state.selectedWasteType,
      value: state.selectedWasteType || undefined,
    },
    {
      label: "Value Added Product",
      checked: !!state.selectedBusinessModel,
      value: state.selectedBusinessModel || undefined,
    },
    {
      label: "Business Models",
      checked: state.selectedBusinessModels.length > 0,
      value:
        state.selectedBusinessModels.length > 0
          ? `${state.selectedBusinessModels.length} selected`
          : undefined,
    },
    {
      label: "Location",
      checked: false,
    },
  ];

  const handleWasteCategoryChange = (category: string) => {
    setWasteCategory(category);
  };

  const handleWasteTypeChange = (type: string) => {
    setWasteType(type);
  };

  const handleBusinessModelSelect = (model: string) => {
    if (!state.selectedWasteType) return;
    if (model === "Wastewater Reuse" || model === "Alternative Proteins")
      return;

    if (state.selectedBusinessModel === model) {
      setBusinessModel("");
    } else {
      setBusinessModel(model);
    }
  };

  const handleSpecificBusinessModelToggle = (model: { type: string; description: string }) => {
    toggleBusinessModel(model, 3);
  };
  
  const handleContinue = () => {
    navigate("/plant-location");
  };

  const handleBack = () => {
    navigate("/goal-setting");
  };

  return (
    <div className="businessModels-container">
      <VerticalNavigation
        title="Business Models"
        description="Pick A Waste Type And What You Want To Do With It"
        tags={tags}
        currentStep={2}
        totalSteps={4}
        onMenuToggle={setIsMenuOpen}
        onBack={handleBack}
      />
      {!isMenuOpen && (
        <div className="businessModels-content">
          <div className="businessModels-inner">
            <div className="businessModels-columns">
              <div className="businessModels-column-left">
                <div className="businessModels-fields-row">
                  <div className="businessModels-field">
                    <h3 className="businessModels-field-title">
                      Waste Category
                    </h3>
                    <Dropdown
                      placeholder="Select a waste category"
                      options={wasteCategories}
                      value={state.selectedWasteCategory}
                      onChange={handleWasteCategoryChange}
                      isLoading={loading}
                    />
                  </div>

                  <div className="businessModels-field">
                    <h3 className="businessModels-field-title">Waste Type</h3>
                    <Dropdown
                      placeholder="Select a waste type"
                      isLoading={loading}
                      options={
                        state.selectedWasteCategory
                          ? wasteTypes[state.selectedWasteCategory] || []
                          : []
                      }
                      value={state.selectedWasteType}
                      onChange={handleWasteTypeChange}
                      disabled={!state.selectedWasteCategory}
                      disabledText="Select waste category first"
                    />
                  </div>
                </div>

                <div className="businessModels-value-added-section">
                  <h3 className="businessModels-field-title">
                    Value added product
                  </h3>
                  <div className="businessModels-value-added-buttons">
                    <StyleButton
                      content="Energy"
                      subtitle=""
                      icon={<Fuel />}
                      isSelected={state.selectedBusinessModel === "Energy"}
                      onClick={() => handleBusinessModelSelect("Energy")}
                      backgroundColorSelected="var(--color-green)"
                      disabled={!state.selectedWasteType}
                      tooltipText="Renewable energy such as biogas or bioelectricity produced from organic waste through anaerobic digestion or combustion, offering a sustainable alternative to fossil fuels."
                    />
                    <StyleButton
                      content="Nutrients"
                      subtitle=""
                      icon={<Leaf />}
                      isSelected={state.selectedBusinessModel === "Nutrients"}
                      onClick={() => handleBusinessModelSelect("Nutrients")}
                      backgroundColorSelected="var(--color-green)"
                      disabled={!state.selectedWasteType}
                      tooltipText="Valuable elements like nitrogen, phosphorus, and potassium recovered from organic waste, used to create fertilizers that enhance soil fertility and crop productivity."
                    />
                    <StyleButton
                      content="Wastewater Reuse"
                      subtitle=""
                      icon={<Droplets />}
                      isSelected={
                        state.selectedBusinessModel === "Wastewater Reuse"
                      }
                      onClick={() =>
                        handleBusinessModelSelect("Wastewater Reuse")
                      }
                      backgroundColorSelected="var(--color-green)"
                      disabled={true}
                      tooltipText="Treated wastewater that is repurposed for agricultural, industrial, or landscape irrigation, reducing freshwater demand and promoting sustainable water resource management."
                    />
                    <StyleButton
                      content="Alternative Proteins"
                      subtitle=""
                      icon={<Bug />}
                      isSelected={
                        state.selectedBusinessModel === "Alternative Proteins"
                      }
                      onClick={() =>
                        handleBusinessModelSelect("Alternative Proteins")
                      }
                      backgroundColorSelected="var(--color-green)"
                      disabled={true}
                      tooltipText="Edible proteins sourced from insects, algae, or microbial biomass grown on organic waste, offering sustainable alternatives to traditional animal-based protein sources."
                    />
                  </div>
                </div>
              </div>

              <div className="businessModels-column-right">
                <div className="businessModels-business-models-box">
                  <div className="businessModels-business-models-header">
                    <h3 className="businessModels-field-title">
                                                                 Select 3 business models
                    </h3>
                  </div>
                  {getCurrentBusinessModels().length > 0 ? (
                    <div className="businessModels-business-models-grid">
                      {getCurrentBusinessModels().map((model) => (
                        <StyleButton
                          key={model.type}
                          content={model.type}
                          isSelected={state.selectedBusinessModels.some(
                            (selected) => selected.type === model.type
                          )}
                          onClick={() =>
                            !isShowingDefault &&
                              handleSpecificBusinessModelToggle(model)
                          }
                          disabled={
                            isShowingDefault ||
                              (!state.selectedBusinessModels.some(
                                (selected) => selected.type === model.type
                              ) &&
                                state.selectedBusinessModels.length >= 3)
                          }
                          tooltipText={model.description}
                          backgroundColorSelected="var(--color-green)"
                        />
                      ))}
                    </div>
                  ) : !state.selectedBusinessModel ? (
                    <div className="businessModels-no-selection-text">
                      No Value Added Product selected yet
                    </div>
                  ) : (
                    <div className="businessModels-no-selection-text">
                      There are no possible business models for the waste type
                      and value added product selected. Please modify your
                      choices.
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="businessModels-button-container">
              <button
                className={`businessModels-continue-button ${
                  state.selectedWasteType &&
                  state.selectedBusinessModel &&
                  state.selectedBusinessModels.length === 3
                    ? "businessModels-continue-button--active"
                    : "businessModels-continue-button--disabled"
                }`}
                onClick={handleContinue}
                disabled={
                  !state.selectedWasteType ||
                  !state.selectedBusinessModel ||
                  state.selectedBusinessModels.length !== 3
                }
              >
                Plant Location →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessModels;

